import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AttributesState } from './attributes.reducer';
import {
  Attribute,
  isSystemAttribute,
  isCustomAttribute,
  isGS1Attribute,
} from '../models/attribute.model';

export const selectAttributesState =
  createFeatureSelector<AttributesState>('attributes');

export const selectAttributes = createSelector(
  selectAttributesState,
  (state) => state.attributes,
);

/**
 * Select system attributes (type is one of ATTRIBUTE_SYSTEM_TYPES).
 * Mirrors React's systemAttributesSelector.
 */
export const selectSystemAttributes = createSelector(
  selectAttributes,
  (attributes): Attribute[] => attributes.filter(isSystemAttribute),
);

/**
 * Select custom attributes (type is 'CUSTOM' and NOT a GS1 identifier code).
 * Mirrors React's customAttributesSelector.
 */
export const selectCustomAttributes = createSelector(
  selectAttributes,
  (attributes): Attribute[] => attributes.filter(isCustomAttribute),
);

/**
 * Select GS1 attributes (type is GS1-related).
 * Mirrors React's gs1AttributesSelector.
 */
export const selectGs1Attributes = createSelector(
  selectAttributes,
  (attributes): Attribute[] => attributes.filter(isGS1Attribute),
);

export const selectAttributesMeta = createSelector(
  selectAttributesState,
  (state) => state.meta,
);

export const selectAttributesLoading = createSelector(
  selectAttributesState,
  (state) => state.isLoading,
);

export const selectAttributesSubmitting = createSelector(
  selectAttributesState,
  (state) => state.isSubmitting,
);

export const selectAttributesDeleting = createSelector(
  selectAttributesState,
  (state) => state.isDeleting,
);

export const selectAttributesExporting = createSelector(
  selectAttributesState,
  (state) => state.isExporting,
);

export const selectAttributesError = createSelector(
  selectAttributesState,
  (state) => state.error,
);

export const selectAttributesLastQuery = createSelector(
  selectAttributesState,
  (state) => state.lastQuery,
);

export const selectGs1AiDefinitions = createSelector(
  selectAttributesState,
  (state) => state.gs1AiDefinitions,
);

export const selectGs1DefinitionsLoading = createSelector(
  selectAttributesState,
  (state) => state.isGs1DefinitionsLoading,
);

export const selectAttributeOptions = createSelector(
  selectAttributesState,
  (state) => state.attributeOptions,
);

export const selectAttributeOptionsLoading = createSelector(
  selectAttributesState,
  (state) => state.isOptionsLoading,
);

// Cached options selectors
export const selectOptionsCache = createSelector(
  selectAttributesState,
  (state) => state.optionsCache,
);

export const selectCachedAttributeOptions = (attributeUuid: string) =>
  createSelector(
    selectOptionsCache,
    (cache) => cache[attributeUuid]?.options ?? [],
  );

export const selectCachedAttributeOptionsLoading = (attributeUuid: string) =>
  createSelector(
    selectOptionsCache,
    (cache) => cache[attributeUuid]?.loading ?? false,
  );

export const selectCachedAttributeOptionsError = (attributeUuid: string) =>
  createSelector(
    selectOptionsCache,
    (cache) => cache[attributeUuid]?.error ?? null,
  );

/** Check if options are already loaded for an attribute */
export const selectCachedAttributeOptionsLoaded = (attributeUuid: string) =>
  createSelector(
    selectOptionsCache,
    (cache) => {
      const entry = cache[attributeUuid];

      return entry !== undefined && entry.options.length > 0;
    },
  );

export const selectAttributesLastExportJobId = createSelector(
  selectAttributesState,
  (state) => state.lastExportJobId,
);
